import type { ReactElement, ReactNode } from 'react';
import { css } from '@emotion/react';
import { Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Textfit } from 'react-textfit';
import { BalanceLabel } from './BalanceLabel';

export type PpBalance = {
  alignment?: 'center' | 'left';
  IconSlot?: React.ReactElement;
  label?: string;
  onLabelClick?: () => void;
  PrimaryAmountSlot?: ReactNode;
  SecondaryAmountSlot?: ReactNode;
};

const headingLMaxFontSizeFallback = 40;

export function Balance({
  alignment = 'center',
  IconSlot,
  label,
  onLabelClick,
  PrimaryAmountSlot,
  SecondaryAmountSlot,
}: PpBalance): ReactElement {
  const theme = useTheme();

  const styles = {
    inner: css`
      position: relative;
    `,
    outer: css`
      text-align: ${alignment};
      width: 100%;
    `,
  };

  const headingLFontSizeFromTheme = theme.typography.headingL?.fontSize?.toString().split('rem')[0];
  const headingLFontSizeInPixel = headingLFontSizeFromTheme
    ? parseFloat(headingLFontSizeFromTheme) * theme.typography.htmlFontSize
    : headingLMaxFontSizeFallback;

  return (
    <Stack css={styles.outer}>
      {label && <BalanceLabel label={label} onLabelClick={onLabelClick} />}
      <div css={styles.inner}>
        <Stack spacing={1}>
          {PrimaryAmountSlot && (
            <Stack justifyContent="center" sx={{ minHeight: theme.spacing(4) }}>
              <Typography
                data-qa="primary-amount"
                sx={{
                  margin: alignment === 'center' ? theme.spacing(0, 8) : undefined,
                }}
                variant="headingL"
              >
                <Textfit max={headingLFontSizeInPixel} min={16} mode="single">
                  {PrimaryAmountSlot}
                </Textfit>
              </Typography>
            </Stack>
          )}
          {SecondaryAmountSlot && (
            <Typography color="text.light" data-qa="secondary-amount" variant="paragraphBodyS">
              {SecondaryAmountSlot}
            </Typography>
          )}
        </Stack>
        {IconSlot}
      </div>
    </Stack>
  );
}
